import { FC, useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import Link from 'next/link';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { SpotifyAlbum } from '../types/spotify';
import { useSpotifyPlayback } from '../contexts/SpotifyPlaybackContext';
import PlaybackStatusMessage from "../components/PlaybackStatusMessage";
import { FavoriteAlbum } from '../types/favorites';
import { compareAlphabetically } from '../utils/sorting';

interface AutocompleteSuggestion {
  id: string;
  name: string;
  type: 'artist' | 'album';
  image?: string;
  subtitle: string;
}

interface DiscographyAlbum {
  id: string;
  name: string;
  album_type: string;
  release_date: string;
  total_tracks: number;
  images: Array<{ url: string; height: number; width: number; }>;
  artists: Array<{ id: string; name: string; }>;
  popularity: number;
  duration_ms: number;
}

interface ArtistDiscographyResponse {
  albums: DiscographyAlbum[];
  total: number;
}

interface CheckLibraryResponse {
  albumsInLibrary: string[];
  albumIdMap: { [spotifyId: string]: number };
}

type SortOption = 'release_date_desc' | 'release_date_asc' | 'name' | 'popularity';
type FilterOption = 'all' | 'album' | 'single' | 'studio_albums';

const SearchPage: FC<{ embedded?: boolean; onArtistAdded?: () => void }> = ({ embedded = false, onArtistAdded }) => {
  const { accessToken, isAuthenticated, login } = useAuth();
  const { showToast } = useNotification();
  const { playAlbum, playArtist } = useSpotifyPlayback();
  const router = useRouter();
  const { q, artist, artistName, artistId, view } = router.query;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [selectedArtist, setSelectedArtist] = useState<AutocompleteSuggestion | null>(null);
  const [discography, setDiscography] = useState<DiscographyAlbum[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [playbackStatuses, setPlaybackStatuses] = useState<{[key: string]: string | null}>({});
  const [albumsInLibrary, setAlbumsInLibrary] = useState<Set<string>>(new Set());
  const [albumIdMap, setAlbumIdMap] = useState<{ [spotifyId: string]: number }>({});
  const [saveStatuses, setSaveStatuses] = useState<{[key: string]: 'saving' | 'saved' | 'error' | null}>({});
  const [exploreStatus, setExploreStatus] = useState<'idle' | 'adding' | 'added' | 'error'>('idle');
  const [isArtistInExploration, setIsArtistInExploration] = useState<boolean>(false);
  const [isArtistInLibrary, setIsArtistInLibrary] = useState<boolean>(false);

  // Discography filters
  const [sortBy, setSortBy] = useState<SortOption>('popularity');
  const [filterBy, setFilterBy] = useState<FilterOption>('studio_albums');

  // Initialize from URL parameters
  useEffect(() => {
    if (q && typeof q === 'string') {
      setSearchQuery(q);
    }
    
    // Handle the new way of passing artist data (from ExplorationQueue)
    if (artistName && artistId && typeof artistName === 'string' && typeof artistId === 'string') {
      const artistData: AutocompleteSuggestion = {
        id: artistId,
        name: artistName,
        type: 'artist',
        subtitle: 'Artist'
      };
      setSelectedArtist(artistData);
      setSearchQuery(artistName);
      fetchDiscography(artistId);
      checkArtistStatus(artistId, artistName);
    }
    // Handle the old way of passing artist data (legacy)
    else if (artist && typeof artist === 'string') {
      try {
        const artistData = JSON.parse(decodeURIComponent(artist));
        setSelectedArtist(artistData);
        fetchDiscography(artistData.id);
        checkArtistStatus(artistData.id, artistData.name);
      } catch (error) {
        console.error('Error parsing artist data from URL:', error);
        // If JSON parsing fails, clear the artist parameter from URL
        router.replace('/explore?section=find', undefined, { shallow: true });
      }
    }
  }, [q, artist, artistName, artistId, view]);

  const checkArtistStatus = async (artistId: string, artistName: string) => {
    try {
      const response = await axios.get<{ inExploration: boolean; inLibrary: boolean }>('/api/search/check-artist', {
        params: {
          spotify_artist_id: artistId,
          artist_name: artistName
        }
      });
      setIsArtistInExploration(response.data.inExploration);
      setIsArtistInLibrary(response.data.inLibrary);
    } catch (error) {
      console.error('Error checking artist status:', error);
    }
  };

  // Debounced autocomplete search
  const debouncedSearch = useCallback(
      debounce(async (query: string) => {
        if (!query.trim() || !accessToken) {
          setSuggestions([]);
          return;
        }

        try {
          const response = await axios.get<AutocompleteSuggestion[]>('/api/search/autocomplete', {
            params: { q: query.trim(), access_token: accessToken }
          });
          setSuggestions(response.data);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        }
      }, 300),
      [accessToken]
  );

  useEffect(() => {
    if (searchQuery) {
      debouncedSearch(searchQuery);
    } else {
      setSuggestions([]);
    }
  }, [searchQuery, debouncedSearch]);

  const fetchDiscography = async (artistId: string, currentFilter?: FilterOption) => {
    if (!accessToken) return;

    setLoading(true);
    try {
      // Use the passed filter or the current state
      const activeFilter = currentFilter || filterBy;
      const includeGroups = activeFilter === 'all' ? 'album,single' : 
                          activeFilter === 'album' || activeFilter === 'studio_albums' ? 'album' : 'single';
      
      console.log('Fetching discography with filter:', activeFilter, 'include_groups:', includeGroups);
      const response = await axios.get<ArtistDiscographyResponse>('/api/search/artist-discography', {
        params: {
          artist_id: artistId,
          access_token: accessToken,
          include_groups: includeGroups,
          limit: 50
        }
      });
      setDiscography(response.data.albums);

      // Check which albums are already in library
      await checkAlbumsInLibrary(response.data.albums.map(album => album.id));
    } catch (error) {
      console.error('Error fetching discography:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAlbumsInLibrary = async (albumIds: string[]) => {
    try {
      const response = await axios.post<CheckLibraryResponse>('/api/albums/check-library', {
        spotify_album_ids: albumIds
      });
      setAlbumsInLibrary(new Set(response.data.albumsInLibrary));
      setAlbumIdMap(response.data.albumIdMap);
    } catch (error) {
      console.error('Error checking albums in library:', error);
    }
  };

  const handleSuggestionClick = (suggestion: AutocompleteSuggestion) => {
    if (suggestion.type === 'artist') {
      setSelectedArtist(suggestion);
      setSearchQuery(suggestion.name);
      setShowSuggestions(false);
      fetchDiscography(suggestion.id);
      checkArtistStatus(suggestion.id, suggestion.name);

      // Update URL
      router.push({
        pathname: '/explore',
        query: { section: 'find', artist: encodeURIComponent(JSON.stringify(suggestion)) }
      }, undefined, { shallow: true });
    } else {
      // Handle album selection if needed
      setSearchQuery(suggestion.name);
      setShowSuggestions(false);
    }
  };

  const handleClearArtist = () => {
    setSelectedArtist(null);
    setDiscography([]);
    setSearchQuery('');
    router.push('/explore?section=find', undefined, { shallow: true });
  };

  const handlePlayArtist = async () => {
    if (!selectedArtist || !accessToken) return;

    setPlaybackStatuses(prev => ({ ...prev, [selectedArtist.id]: null }));

    const result = await playArtist(selectedArtist.id);

    if (result.success) {
      setPlaybackStatuses(prev => ({ ...prev, [selectedArtist.id]: "Now playing artist" }));
    } else {
      setPlaybackStatuses(prev => ({ ...prev, [selectedArtist.id]: `Error: ${result.message}` }));
    }
  };

  const handlePlayAlbum = async (album: DiscographyAlbum) => {
    setPlaybackStatuses(prev => ({ ...prev, [album.id]: null }));

    if (accessToken) {
      const result = await playAlbum(album.id);

      if (result.success) {
        setPlaybackStatuses(prev => ({ ...prev, [album.id]: "Now playing" }));
      } else {
        setPlaybackStatuses(prev => ({ ...prev, [album.id]: `Error: ${result.message}` }));
      }
    }
  };

  const handleSaveAlbum = async (album: DiscographyAlbum) => {
    setSaveStatuses(prev => ({ ...prev, [album.id]: 'saving' }));

    try {
      const response = await axios.post<FavoriteAlbum>('/api/albums/favorites', {
        spotify_album_id: album.id,
        name: album.name,
        artist: album.artists[0].name,
        spotify_artist_id: album.artists[0].id,
        image_url: album.images[0]?.url,
        release_date: album.release_date,
        popularity: album.popularity
      });

      // Get the saved album data with internal ID
      const savedAlbum = response.data;

      setSaveStatuses(prev => ({ ...prev, [album.id]: 'saved' }));
      setAlbumsInLibrary(prev => {
        const newSet = new Set(prev);
        newSet.add(album.id);
        return newSet;
      });

      // Update the album ID mapping with the new internal ID
      setAlbumIdMap(prev => ({
        ...prev,
        [album.id]: savedAlbum.id
      }));

      // Clear the saved status after 3 seconds
      setTimeout(() => {
        setSaveStatuses(prev => ({ ...prev, [album.id]: null }));
      }, 3000);

    } catch (error: any) {
      console.error('Error saving album:', error);
      setSaveStatuses(prev => ({ ...prev, [album.id]: 'error' }));
      
      // Clear error status after 5 seconds
      setTimeout(() => {
        setSaveStatuses(prev => ({ ...prev, [album.id]: null }));
      }, 5000);
    }
  };

  const handleAddToExplore = async (artistName: string) => {
    if (!selectedArtist) return;
    
    try {
      setExploreStatus('adding');
      
      // Parse followers from subtitle (e.g. "1,234,567 followers")
      const followers = selectedArtist.subtitle 
          ? parseInt(selectedArtist.subtitle.replace(/[^\d]/g, '')) 
          : null;

      await axios.post('/api/exploration', {
        artist_name: selectedArtist.name,
        spotify_artist_id: selectedArtist.id,
        artist_image_url: selectedArtist.image,
        artist_followers: followers,
        status: 'Backlog'
      });

      setExploreStatus('added');
      setIsArtistInExploration(true);
      onArtistAdded?.();
      showToast(`${selectedArtist.name} added to your exploration queue!`, 'success');
      
      // Reset status after 3 seconds
      setTimeout(() => setExploreStatus('idle'), 3000);
    } catch (error) {
      console.error('Error adding to exploration queue:', error);
      setExploreStatus('error');
      showToast('Failed to add to exploration queue', 'error');
      
      // Reset status after 3 seconds
      setTimeout(() => setExploreStatus('idle'), 3000);
    }
  };

  // Filter and sort discography
  // Helper function to detect if an album is likely a studio album
  const isLikelyStudioAlbum = (album: DiscographyAlbum): boolean => {
    if (album.album_type !== 'album') return false;
    
    const name = album.name.toLowerCase();
    
    // Common patterns for non-studio albums
    const nonStudioPatterns = [
      /\blive\b/,
      /\bconcert\b/,
      /\btour\b/,
      /\bgreatest hits\b/,
      /\bbest of\b/,
      /\bcollection\b/,
      /\bcompilation\b/,
      /\banthology\b/,
      /\bessential\b/,
      /\bselected\b/,
      /\bunleashed\b/,
      /\bunplugged\b/,
      /\bacoustic\b/,
      /\bmtv\b/,
      /\bradio\b/,
      /\bsessions?\b/,
      /\b\d{4}\s*-\s*\d{4}\b/, // Year ranges like "1990-2000"
      /\bdeluxe\b/,
      /\bexpanded\b/,
      /\bspecial edition\b/,
      /\banniversary edition\b/,
      /\bbbc\b/,
      /\blimited\b/,
      /\brare\b/,
      /\bb-sides?\b/,
      /\bouttakes?\b/,
      /\bdemos?\b/,
      /\btributes?\b/,
      /\bcovers?\b/,
      /\bkaraoke\b/,
      /\binstrumental\b/,
      /\boriginal soundtrack\b/,
      /\bost\b/,
      /\bsoundtrack\b/,
      /\bmotion picture\b/
    ];
    
    return !nonStudioPatterns.some(pattern => pattern.test(name));
  };

  // Helper function to get sort key by removing leading articles
  const getSortKey = (name: string): string => {
    const articles = ['the ', 'a ', 'an '];
    const lowerName = name.toLowerCase();
    
    for (const article of articles) {
      if (lowerName.startsWith(article)) {
        return name.substring(article.length);
      }
    }
    
    return name;
  };

  // Filter and sort discography
  const filteredAndSortedDiscography = discography
  .filter(album => {
    if (filterBy === 'studio_albums') {
      return isLikelyStudioAlbum(album);
    }
    return true;
  })
  .sort((a, b) => {
    switch (sortBy) {
      case 'release_date_desc':
        return new Date(b.release_date).getTime() - new Date(a.release_date).getTime();
      case 'release_date_asc':
        return new Date(a.release_date).getTime() - new Date(b.release_date).getTime();
      case 'name':
        return compareAlphabetically(a.name, b.name);
      case 'popularity':
        // Handle cases where popularity might be undefined
        const aPopularity = a.popularity || 0;
        const bPopularity = b.popularity || 0;
        return bPopularity - aPopularity;
      default:
        return 0;
    }
  });

  // ... existing code until the Discography Filters section ...

  if (!isAuthenticated) {
    return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <h1 className="text-3xl font-bold mb-8 text-center">Search Music</h1>
          <p className="text-lg mb-8 text-center">
            Please login to search for artists and albums.
          </p>
          <button
              onClick={login}
              className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-full text-lg transition duration-300"
          >
            Login with Spotify
          </button>
        </div>
    );
  }

  const formatDuration = (ms: number) => {
    const minutes = Math.round(ms / 60000);
    
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${remainingMinutes}m`;
    }
    
    return `${minutes}m`;
  };

  return (
      <div className="page-container">
        {!embedded && <h1 className="text-3xl font-bold mb-3 text-center">Search Music</h1>}

        {/* Search Box with Autocomplete */}
        <div className="flex items-start gap-2 mb-3">
          <div className="relative flex-1 min-w-0">
            <input
              type="text"
              placeholder="Search for artists, albums..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="w-full border-2 border-gray-300 rounded-full px-6 py-3 focus:outline-none focus:border-green-500"
            />

          {/* Autocomplete Suggestions */}
          {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto">
                {suggestions.map((suggestion) => (
                    <div
                        key={`${suggestion.type}-${suggestion.id}`}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="flex items-center p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div>
                        <div className="font-semibold">{suggestion.name}</div>
                        <div className="text-sm text-gray-600">
                          {suggestion.type === 'artist' ? '' : '💿'} {suggestion.subtitle}
                        </div>
                      </div>
                    </div>
                ))}
              </div>
          )}
          </div>
          <button
            onClick={handleClearArtist}
            disabled={!searchQuery && !selectedArtist}
            className="min-h-12 shrink-0 bg-gray-500 hover:bg-gray-600 disabled:opacity-40 text-white px-4 rounded-full"
          >
            Clear
          </button>
        </div>

        {/* Artist Discography View */}
        {selectedArtist && (
            <div>
              {/* Artist Header */}
              <div className="compact-panel mb-3">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="flex items-center min-w-0">
                    <div className="min-w-0">
                      <h2 className="text-2xl font-bold">{selectedArtist.name}</h2>
                      <p className="text-gray-600">{selectedArtist.subtitle}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:flex gap-2">
                    <button
                        onClick={handlePlayArtist}
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                    >
                      Play Artist
                    </button>
                    <button
                        onClick={() => handleAddToExplore(selectedArtist.name)}
                        disabled={exploreStatus !== 'idle' || isArtistInLibrary}
                        className={`${
                            exploreStatus === 'added' || isArtistInExploration
                                ? 'bg-green-500 hover:bg-green-600' 
                                : exploreStatus === 'error'
                                    ? 'bg-red-500 hover:bg-red-600'
                                    : isArtistInLibrary
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-purple-500 hover:bg-purple-600'
                        } text-white px-4 py-2 rounded disabled:opacity-75 transition-colors duration-200`}
                    >
                      {isArtistInLibrary && 'In Library'}
                      {!isArtistInLibrary && exploreStatus === 'idle' && (isArtistInExploration ? '✓ Exploring' : 'Add To Explore')}
                      {!isArtistInLibrary && exploreStatus === 'adding' && 'Adding...'}
                      {!isArtistInLibrary && exploreStatus === 'added' && '✓ Added'}
                      {!isArtistInLibrary && exploreStatus === 'error' && 'Failed'}
                    </button>
                  </div>
                </div>
                <PlaybackStatusMessage status={playbackStatuses[selectedArtist.id]} />
              </div>

              {/* Discography Filters */}
              <div className="compact-panel mb-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-2 sm:flex-1">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Sort by</label>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as SortOption)}
                        className="w-full border border-gray-300 rounded px-3 py-1"
                    >
                      <option value="popularity">Popularity</option>
                      <option value="release_date_desc">Newest First</option>
                      <option value="release_date_asc">Oldest First</option>
                      <option value="name">Name</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-2 sm:flex-[1.4]">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Show</label>
                    <select
                        value={filterBy}
                        onChange={(e) => {
                          const newFilter = e.target.value as FilterOption;
                          setFilterBy(newFilter);
                          if (selectedArtist) {
                            // Clear existing discography to avoid showing stale data
                            setDiscography([]);
                            fetchDiscography(selectedArtist.id, newFilter);
                          }
                        }}
                        className="w-full border border-gray-300 rounded px-3 py-1"
                    >
                      <option value="studio_albums">Studio Albums Only</option>
                      <option value="album">All Albums (including live & compilations)</option>
                      <option value="all">All Releases</option>
                    </select>
                  </div>

                  <div className="text-sm text-gray-600 whitespace-nowrap sm:ml-auto">
                    Showing {filteredAndSortedDiscography.length} results
                  </div>
                </div>
              </div>

              {/* Discography Grid */}
              {loading ? (
                  <p className="text-center py-8">Loading discography...</p>
              ) : (
                  <div className="compact-panel p-0 divide-y overflow-hidden">
                    {filteredAndSortedDiscography.map(album => (
                        <div key={album.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 items-center p-2 bg-white">
                          <div className="min-w-0">
                            <h3 className="font-semibold truncate">{album.name}</h3>
                            <p className="text-gray-500 text-xs truncate">
                              <span className="text-sm font-bold text-green-300">{album.release_date.substring(0, 4)}</span> • {album.album_type} • {album.total_tracks} tracks • {formatDuration(album.duration_ms)} • Popularity: {album.popularity}
                            </p>
                          </div>
                            <div className="flex flex-wrap gap-1 justify-end">
                              <button
                                  onClick={() => handlePlayAlbum(album)}
                                  className="compact-button bg-green-500 hover:bg-green-600 text-white px-3 rounded text-sm"
                              >
                                Play
                              </button>
                              
                              {!albumsInLibrary.has(album.id) && (
                                <button
                                    onClick={() => handleSaveAlbum(album)}
                                    disabled={saveStatuses[album.id] === 'saving'}
                                    className={`compact-button px-3 rounded text-sm transition-colors ${
                                      saveStatuses[album.id] === 'saving'
                                        ? 'bg-gray-400 text-white cursor-not-allowed'
                                        : saveStatuses[album.id] === 'saved'
                                        ? 'bg-green-500 text-white'
                                        : saveStatuses[album.id] === 'error'
                                        ? 'bg-red-500 text-white'
                                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                                    }`}
                                >
                                  {saveStatuses[album.id] === 'saving'
                                    ? 'Adding...'
                                    : saveStatuses[album.id] === 'saved'
                                    ? '✓ Added'
                                    : saveStatuses[album.id] === 'error'
                                    ? 'Error'
                                    : 'Add to Library'
                                  }
                                </button>
                              )}

                              {albumsInLibrary.has(album.id) && albumIdMap[album.id] && (
                                <Link href={`/albums/${albumIdMap[album.id]}`}>
                                  <span className="inline-flex items-center min-h-9 px-2 bg-green-100 text-green-700 hover:bg-green-200 rounded text-sm cursor-pointer border border-green-300">
                                    Details
                                  </span>
                                </Link>
                              )}
                            </div>
                            <PlaybackStatusMessage status={playbackStatuses[album.id]} />
                        </div>
                    ))}
                  </div>
              )}
            </div>
        )}
      </div>
  );
};

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default SearchPage;
